/**
 * ResolveDialog — council-side closure dialog for a demand (2-level model).
 *
 * Four outcome cards (the council-initiated leaves; Withdrawal is requestor-
 * initiated via WithdrawDialog):
 *   • Promoted  (success / green)  — graduated into a distinct Product [closure_type: promoted]
 *   • Rejected  (danger / red)     — council decided not to pursue      [closure_type: rejected]
 *   • Merged    (neutral / gray)   — folded into another demand/product [closure_type: other]
 *                                    REVEALS a merge-target-id input below the grid
 *   • Satisfied (info / blue)      — delivered in place / already satisfied / trifle [other]
 *
 * Required resolution notes textarea below the cards.
 *
 * Confirm is disabled until a type is picked, notes.trim() is non-empty, and (for
 * merged) merge_target_id is a positive integer. Payload is trimmed; merge_target_id
 * is sent only when type === 'merged'. Backend enforces the close rules
 * (non-continuing clears the team; promoted needs Resourcing + a real bucket).
 */

import { ToldLine } from './ToldLine'
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Textarea,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components'
import { useResolveDemand, useChangeResolution, useDemands } from '../hooks/useDemands'
import { apiErrorMessage } from '../lib/apiError'
import { parseStageWarnings, type StageWarnings } from '../lib/stageWarnings'
import { bayerColors, bucketColors } from '../theme'
import type { Demand, DemandResolveInput, ResolutionType } from '../types/demand'
import { StageWarningDialog } from './StageWarningDialog'

type Tone = 'success' | 'info' | 'danger' | 'neutral' | 'warning'

interface ResolutionMeta {
  key: ResolutionType
  name: string
  desc: string
  tone: Tone
}

export const RESOLUTION_TYPES: ResolutionMeta[] = [
  { key: 'promoted', name: 'Promoted', desc: 'Graduated into a distinct Product', tone: 'success' },
  // The three Resolved leaves. They differ only in HOW the need was met, so
  // they share a tone and sit together — the reader is picking a shade of the
  // same answer, not between four unrelated ones.
  {
    key: 'resolved_staffed',
    name: 'Resolved · Staffed',
    desc: 'The council staffed it and the team delivered',
    tone: 'info',
  },
  {
    key: 'resolved_self_supported',
    name: 'Resolved · Self-supported',
    desc: 'The requestor resourced it themselves',
    tone: 'info',
  },
  {
    key: 'satisfied',
    name: 'Resolved · Other',
    desc: 'Met some other way — delivered in place, or already satisfied elsewhere',
    tone: 'info',
  },
  { key: 'rejected', name: 'Rejected', desc: 'Council decided not to pursue', tone: 'danger' },
  { key: 'merged', name: 'Merged', desc: 'Folded into another demand', tone: 'neutral' },
]

// Accent colors here re-use the canonical priority bucket palette from
// theme.ts so a future palette swap propagates. The border/bg/text are
// localized tone variants (light hue + readable text) that don't have a
// reusable home yet — leave inline until we extract a `statePalette`.
const TONE_COLORS: Record<Tone, { border: string; bg: string; text: string; accent: string }> = {
  success: { border: '#bbf7d0', bg: '#f0fdf4', text: '#166534', accent: bucketColors.quickWins },
  info: { border: '#bfdbfe', bg: '#eff6ff', text: '#1e40af', accent: '#2563eb' },
  danger: { border: '#fecaca', bg: '#fef2f2', text: '#991b1b', accent: bucketColors.highPriority },
  neutral: { border: '#e5e7eb', bg: '#f9fafb', text: '#374151', accent: bucketColors.declined },
  warning: { border: '#fde68a', bg: '#fefce8', text: '#854d0e', accent: bucketColors.parkingLot },
}

const useStyles = makeStyles({
  surface: {
    maxWidth: '560px',
    width: '100%',
  },
  sub: {
    fontSize: '12px',
    color: '#616161',
    marginTop: '4px',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#424242',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    margin: '4px 0 8px',
  },
  required: {
    color: '#c62828',
    marginLeft: '2px',
  },
  caption: {
    fontSize: '12px',
    color: '#757575',
    margin: '0 0 6px',
  },
  grid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    padding: '10px 14px',
    borderRadius: '8px',
    borderTopWidth: '1.5px',
    borderRightWidth: '1.5px',
    borderBottomWidth: '1.5px',
    borderLeftWidth: '1.5px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    background: '#ffffff',
    cursor: 'pointer',
    textAlign: 'left',
    transitionProperty: 'background, border-color, box-shadow',
    transitionDuration: '120ms',
    ':hover': {
      borderTopColor: '#9ca3af',
      borderRightColor: '#9ca3af',
      borderBottomColor: '#9ca3af',
      borderLeftColor: '#9ca3af',
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: bayerColors.navyMid,
      outlineOffset: '2px',
    },
  },
  cardSelected: {
    boxShadow: '0 0 0 2px rgba(43,107,166,0.2)',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 600,
  },
  cardDesc: {
    fontSize: '12px',
    color: '#525252',
  },
  mergeBlock: {
    marginTop: '12px',
  },
  mergeInput: {
    width: '100%',
  },
  mergeSelected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '4px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${bayerColors.navyMid}`,
    background: '#f3f6fb',
    fontSize: '13px',
  },
  mergeResults: {
    marginTop: '4px',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    maxHeight: '180px',
    overflowY: 'auto',
  },
  mergeResultRow: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    fontSize: '13px',
    border: 'none',
    borderBottom: '1px solid #f0f0f0',
    background: '#ffffff',
    cursor: 'pointer',
    ':hover': { background: '#f3f6fb' },
  },
  mergeEmpty: {
    padding: '8px 12px',
    fontSize: '12px',
    color: '#757575',
  },
  notes: {
    width: '100%',
    marginTop: '4px',
  },
  footStatus: {
    fontSize: '12px',
    color: '#757575',
    flex: 1,
  },
})

export interface ResolveDialogProps {
  open: boolean
  demandId: number
  demandRef: string
  demandTitle: string
  onClose: () => void
  onResolved?: (demand: Demand) => void
  /** When 'change', uses PATCH to update resolution type on already-resolved demand */
  mode?: 'resolve' | 'change'
}

export function ResolveDialog({
  open,
  demandId,
  demandRef,
  demandTitle,
  onClose,
  onResolved,
  mode = 'resolve',
}: ResolveDialogProps) {
  const styles = useStyles()
  const [type, setType] = useState<ResolutionType | null>(null)
  const [notes, setNotes] = useState('')
  // Merge target — picked from a search, not a raw id. mergeSel holds the
  // chosen demand; mergeQuery drives the (debounced) search.
  const [mergeSel, setMergeSel] = useState<{ id: number; label: string } | null>(null)
  const [mergeQuery, setMergeQuery] = useState('')
  const [mergeQueryDebounced, setMergeQueryDebounced] = useState('')
  const [error, setError] = useState<string | null>(null)
  // A continuing closure (Promoted / Merged) on an incomplete demand 409s with
  // the missing fields; these hold that dialog and the body to re-send (#1149).
  const [stageWarnings, setStageWarnings] = useState<StageWarnings | null>(null)
  const [pendingBody, setPendingBody] = useState<DemandResolveInput | null>(null)
  const resolveMutation = useResolveDemand()
  const changeMutation = useChangeResolution()
  const mutation = mode === 'change' ? changeMutation : resolveMutation

  // Reset form whenever the dialog reopens for a new demand.
  useEffect(() => {
    if (open) {
      setType(null)
      setNotes('')
      setMergeSel(null)
      setMergeQuery('')
      setMergeQueryDebounced('')
      setError(null)
      setStageWarnings(null)
      setPendingBody(null)
    }
  }, [open, demandId])

  // Debounce the merge-target search (250ms) so we don't fire a request
  // per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setMergeQueryDebounced(mergeQuery.trim()), 250)
    return () => clearTimeout(t)
  }, [mergeQuery])

  const trimmedNotes = notes.trim()
  const mergeValid = !!mergeSel
  const canConfirm =
    !!type && trimmedNotes.length > 0 && (type !== 'merged' || mergeValid)

  const isPending = mutation.isPending
  const resolutionName = RESOLUTION_TYPES.find((r) => r.key === type)?.name

  // What the footer says, derived from the condition that is actually unmet.
  //
  // It used to be a single fixed sentence for every not-ready state: "Pick a
  // type and add notes". So choosing Merged and writing notes — both done —
  // still told you to do them, while never mentioning the merge target it was
  // really waiting for. Being told to redo finished work is worse than being
  // told nothing.
  const statusHint = !type
    ? 'Pick an outcome to continue'
    : !trimmedNotes.length
      ? 'Add resolution notes to continue'
      : type === 'merged' && !mergeValid
        ? 'Choose the merge target to continue'
        : 'Ready to resolve'

  // Search demands for the merge target. Council/admin see all demands;
  // exclude this demand itself (can't merge into yourself).
  const { data: mergeSearch } = useDemands({
    search: mergeQueryDebounced,
    page_size: 8,
    enabled: type === 'merged' && mergeQueryDebounced.length >= 2,
  })
  const mergeOptions = (mergeSearch?.items ?? []).filter((d) => d.id !== demandId)

  // Post the resolve, routing a 409 stage-warning to the confirm dialog instead
  // of the flat error line. A continuing closure on an incomplete demand answers
  // with the missing fields, expecting them back in `acknowledged_warnings` —
  // the same confirm-and-continue contract as a stage transition. Before #1149
  // that 409 fell through to `setError`, whose message even promised a continue
  // path the dialog never offered, so Promoted/Merged were unreachable on any
  // demand missing one of the 26 stage expectations (i.e. most of them).
  function submit(body: DemandResolveInput) {
    setError(null)
    mutation.mutate(
      { id: demandId, body },
      {
        onSuccess: (demand) => {
          onResolved?.(demand)
          onClose()
        },
        onError: (err: unknown) => {
          const warnings = parseStageWarnings(err)
          if (warnings) {
            setPendingBody(body)
            setStageWarnings(warnings)
            return
          }
          // Not a warning — a real block (B4 caretaker, rule-(c) precondition)
          // or a bare failure. Show the backend's reason, not "status code 400".
          setError(apiErrorMessage(err, 'Failed to resolve demand'))
        },
      },
    )
  }

  function handleConfirm() {
    if (!type || !canConfirm || isPending) return
    const body: DemandResolveInput = {
      resolution_type: type,
      resolution_notes: trimmedNotes,
    }
    if (type === 'merged' && mergeSel) {
      body.merge_target_id = mergeSel.id
    }
    submit(body)
  }

  function continuePastWarnings(codes: string[]) {
    if (!pendingBody) return
    setStageWarnings(null)
    submit({ ...pendingBody, acknowledged_warnings: codes })
  }

  function handleOpenChange(_e: unknown, data: { open: boolean }) {
    if (!data.open && !isPending) onClose()
  }

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange} modalType="modal">
      <DialogSurface className={styles.surface} aria-label={mode === 'change' ? 'Change resolution' : 'Resolve demand'}>
        <DialogBody>
          <DialogTitle>
            {mode === 'change' ? 'Change resolution type' : 'Resolve demand'}
            <div className={styles.sub} data-testid="resolve-demand-sub">
              {demandRef} · {demandTitle}
            </div>
          </DialogTitle>
          <DialogContent>
            <div className={styles.sectionLabel}>How is this resolving?</div>
            <div className={styles.grid} role="radiogroup" aria-label="Resolution type">
              {RESOLUTION_TYPES.map((rt) => {
                const palette = TONE_COLORS[rt.tone]
                const selected = type === rt.key
                return (
                  <button
                    key={rt.key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`resolve-card-${rt.key}`}
                    data-tone={rt.tone}
                    className={mergeClasses(styles.card, selected && styles.cardSelected)}
                    style={{
                      borderColor: selected ? palette.accent : palette.border,
                      background: selected ? palette.bg : '#ffffff',
                      color: palette.text,
                    }}
                    onClick={() => setType(rt.key)}
                    disabled={isPending}
                  >
                    <span className={styles.cardName} style={{ color: palette.accent }}>
                      {rt.name}
                    </span>
                    <span className={styles.cardDesc}>{rt.desc}</span>
                  </button>
                )
              })}
            </div>

            {type === 'merged' ? (
              <div className={styles.mergeBlock} data-testid="merge-target-block">
                <div className={styles.sectionLabel}>
                  Merged into <span className={styles.required}>*</span>
                </div>
                {mergeSel ? (
                  <div className={styles.mergeSelected} data-testid="merge-target-selected">
                    <span>{mergeSel.label}</span>
                    <Button
                      appearance="subtle"
                      size="small"
                      onClick={() => {
                        setMergeSel(null)
                        setMergeQuery('')
                      }}
                      disabled={isPending}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className={styles.caption}>
                      Search the demand this one is being folded into — by ID (e.g. FA1-023) or
                      title.
                    </p>
                    <Input
                      className={styles.mergeInput}
                      placeholder="Search demands by ID or title…"
                      value={mergeQuery}
                      onChange={(_, data) => setMergeQuery(data.value)}
                      disabled={isPending}
                      aria-label="Search merge target"
                      data-testid="merge-target-search"
                    />
                    {mergeQueryDebounced.length >= 2 ? (
                      <div
                        className={styles.mergeResults}
                        role="listbox"
                        data-testid="merge-target-results"
                      >
                        {mergeOptions.length === 0 ? (
                          <div className={styles.mergeEmpty}>No matching demands</div>
                        ) : (
                          mergeOptions.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              role="option"
                              aria-selected={false}
                              className={styles.mergeResultRow}
                              data-testid={`merge-option-${d.id}`}
                              disabled={isPending}
                              onClick={() => {
                                setMergeSel({
                                  id: d.id,
                                  label: `${d.demand_id ?? `#${d.id}`} — ${d.title}`,
                                })
                                setMergeQuery('')
                              }}
                            >
                              <strong>{d.demand_id ?? `#${d.id}`}</strong> — {d.title}
                            </button>
                          ))
                        )}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            <div className={styles.sectionLabel} style={{ marginTop: '16px' }}>
              Resolution notes <span className={styles.required}>*</span>
            </div>
            <p className={styles.caption}>
              For audit trail. Cite specific decisions / outcomes / next-step owners as relevant.
            </p>
            <Textarea
              className={styles.notes}
              rows={4}
              placeholder="Why this resolution? What's the team's next step?"
              value={notes}
              onChange={(_, data) => setNotes(data.value)}
              disabled={isPending}
              aria-label="Resolution notes"
              data-testid="resolve-notes"
            />

            {mode === 'resolve' && <ToldLine channel="email">pending and approved applicants</ToldLine>}

            {error ? (
              <p style={{ color: '#c62828', fontSize: '12px', marginTop: '8px' }} role="alert">
                {error}
              </p>
            ) : null}
          </DialogContent>
          <DialogActions>
            <span className={styles.footStatus} data-testid="resolve-status">
              {statusHint}
            </span>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isPending}
              data-testid="resolve-cancel"
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={!canConfirm || isPending}
              data-testid="resolve-confirm"
            >
              {isPending
                ? (mode === 'change' ? 'Updating…' : 'Resolving…')
                : (mode === 'change' ? 'Update resolution' : 'Resolve')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
      <StageWarningDialog
        warnings={stageWarnings}
        toStage="closed"
        title={`Close as ${resolutionName ?? 'this'} anyway?`}
        busy={isPending}
        onContinue={continuePastWarnings}
        onGoFix={() => {
          setStageWarnings(null)
          setPendingBody(null)
        }}
      />
    </>
  )
}

export default ResolveDialog
